import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminActivityLogs = () => {
  const { getAuthHeaders } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const params = filter ? { resource: filter } : {};
        const res = await axios.get(`${API}/activity-logs`, { headers: getAuthHeaders(), params });
        setLogs(res.data);
      } catch (error) {
        console.error("Failed to fetch logs");
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [getAuthHeaders, filter]);

  const getActionColor = (action) => {
    const colors = {
      create: "bg-green-500/20 text-green-400",
      update: "bg-blue-500/20 text-blue-400",
      delete: "bg-red-500/20 text-red-400",
      login: "bg-purple-500/20 text-purple-400",
      update_status: "bg-yellow-500/20 text-yellow-400",
      adjust: "bg-orange-500/20 text-orange-400",
    };
    return colors[action] || "bg-slate-500/20 text-slate-400";
  };

  return (
    <div className="space-y-6" data-testid="admin-activity-logs">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-heading font-semibold text-white">Activity Logs</h2>
        <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 bg-slate-700 border-slate-600">
            <SelectValue placeholder="All Resources" />
          </SelectTrigger>
          <SelectContent className="bg-slate-700 border-slate-600">
            <SelectItem value="all">All Resources</SelectItem>
            <SelectItem value="auth">Auth</SelectItem>
            <SelectItem value="products">Products</SelectItem>
            <SelectItem value="orders">Orders</SelectItem>
            <SelectItem value="users">Users</SelectItem>
            <SelectItem value="drivers">Drivers</SelectItem>
            <SelectItem value="categories">Categories</SelectItem>
            <SelectItem value="inventory">Inventory</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">No activity logs</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-300">Timestamp</TableHead>
                  <TableHead className="text-slate-300">User</TableHead>
                  <TableHead className="text-slate-300">Action</TableHead>
                  <TableHead className="text-slate-300">Resource</TableHead>
                  <TableHead className="text-slate-300">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} className="border-slate-700">
                    <TableCell className="text-slate-400 text-sm">
                      {new Date(log.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-white">{log.username}</TableCell>
                    <TableCell>
                      <Badge className={getActionColor(log.action)}>{log.action}</Badge>
                    </TableCell>
                    <TableCell className="text-slate-300 capitalize">{log.resource}</TableCell>
                    <TableCell className="text-slate-400 text-sm max-w-xs truncate">
                      {log.details || log.resource_id?.slice(0, 8) || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminActivityLogs;
